import React, { useState, useRef } from 'react';
import { FormControl, FormGroup, ControlLabel, Button, Modal } from 'react-bootstrap';
import NotificationSystem from 'react-notification-system';
import Api from '../Utils/api';
import FeedbackHandler from './feedbackHandler';

const IssueReportForm = () => {
  const [formData, setFormData] = useState({
    issueType: '',
    userEmail: '',
    userRole: '',
    browserName: '',
    comments: '',
    attachments: []
  });

  const [isIssueTypeSelected, setIsIssueTypeSelected] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFileName, setExportFileName] = useState('');
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const notificationRef = useRef();

  const addNotification = (message, level = 'success') => {
    if (notificationRef.current) {
      notificationRef.current.addNotification({
        message,
        level,
        position: 'tr',
        autoDismiss: 4,
      });
    }
  };

  const userRole = sessionStorage.getItem("userRole");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newFiles]
    }));
  };

  const removeAttachment = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    const payload = new FormData();

    payload.append('issueType', formData.issueType);
    payload.append('userEmail', formData.userEmail);
    payload.append('userRole', formData.userRole);
    payload.append('browserName', formData.browserName);
    payload.append('comments', formData.comments);
    formData.attachments.forEach((file) => payload.append('attachments', file));
    const attachmentNames = formData.attachments.map((f) => f.name).join(', ');
    payload.append('attachmentName', attachmentNames);

    try {
      await Api.post('ComplainFeedback/AddToComplainFeedback', payload);
      addNotification('Form submitted successfully!', 'success');
      setFormData({
        issueType: '',
        userEmail: '',
        userRole: '',
        browserName: '',
        comments: '',
        attachments: []
      });
      setIsIssueTypeSelected(false);
    } catch (error) {
      addNotification('Submission failed. Please try again.', 'error');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleExport = async () => {
    if (!exportFileName) {
      addNotification("Please enter a file name.", "error");
      return;
    }

    setLoadingExport(true);
    try {
      const response = await FeedbackHandler.exportFeedback();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", exportFileName + ".xlsx");
      document.body.appendChild(link);
      link.click();
      setShowExportModal(false);
    } catch (error) {
      addNotification("Export failed. Please try again.", "error");
    } finally {
      setLoadingExport(false);
    }
  };

  const userRoles = [
    { label: '360Admin', value: '360admin' },
    { label: 'Internal', value: 'internal' },
    { label: 'Sales Rep', value: 'sales_rep' },
    { label: 'Branch Admin', value: 'branch_admin' },
    { label: 'Distributor User', value: 'distributorUser' }
  ];

  const browserOptions = [
    { label: 'Chrome', value: 'chrome' },
    { label: 'Firefox', value: 'firefox' },
    { label: 'Edge', value: 'edge' },
    { label: 'Internet Explorer', value: 'internetExplorer' },
    { label: 'Safari', value: 'safari' },
    { label: 'Opera', value: 'pera' },
    { label: 'Other (Please specify in comments)', value: '' }
  ];

  const notificationStyle = {
    NotificationItem: {
      DefaultStyle: {
        margin: '10px 5px 2px 1px',
        padding: '15px',
        wordBreak: 'break-word'
      },
    },
  };

  return (
    <>
    <div>
        {userRole === '360 Admin' && (
        <Button bsStyle="primary"
        className="export-button pull-right" onClick={() => setShowExportModal(true)}>
          Export as .csv
        </Button>
      )}
      <br/>
    </div>
      <NotificationSystem ref={notificationRef} style={notificationStyle} />
      <div>
<form onSubmit={handleSubmit} encType="multipart/form-data" style={{marginTop:"20px"}}>
  {/* Row 1: Email + User Role */}
  <div className="row">
    <div className="col-md-6">
      <FormGroup>
        <ControlLabel>Email address</ControlLabel>
        <FormControl
          type="email"
          name="userEmail"
          value={formData.userEmail}
          onChange={handleChange}
        />
      </FormGroup>
    </div>
    <div className="col-md-6">
      <FormGroup>
        <ControlLabel>User Role</ControlLabel>
        <FormControl
          componentClass="select"
          name="userRole"
          value={formData.userRole}
          onChange={handleChange}
        >
          <option value="">Select...</option>
          {userRoles.map((role, idx) => (
            <option key={idx} value={role.value}>{role.label}</option>
          ))}
        </FormControl>
      </FormGroup>
    </div>
  </div>

  {/* Row 2: Issue Type + Browser */}
  <div className="row">
    <div className="col-md-6">
      <FormGroup>
        <ControlLabel>Issue Type *</ControlLabel>
        <FormControl
          componentClass="select"
          name="issueType"
          value={formData.issueType}
          onChange={(e) => {
            handleChange(e);
            setIsIssueTypeSelected(e.target.value !== '');
          }}
        >
          <option value="">Select...</option>
          <option value="bug">Bug</option>
          <option value="feedback">Feedback</option>
          <option value="question">Question</option>
        </FormControl>
      </FormGroup>
    </div>
    <div className="col-md-6">
      <FormGroup>
        <ControlLabel>Which browser did you use?</ControlLabel>
        <FormControl
          componentClass="select"
          name="browserName"
          value={formData.browserName}
          onChange={handleChange}
        >
          <option value="">Select...</option>
          {browserOptions.map((browser, idx) => (
            <option key={idx} value={browser.value}>{browser.label}</option>
          ))}
        </FormControl>
      </FormGroup>
    </div>
  </div>

  {/* Row 3: Comments + Attachments (each 50% width) */}
  <div className="row">
    <div className="col-md-6">
      <FormGroup>
        <ControlLabel>Comments</ControlLabel>
        <FormControl
          componentClass="textarea"
          name="comments"
          rows="3"
          value={formData.comments}
          onChange={handleChange}
        />
      </FormGroup>
    </div>
  </div>
  <div className="row">
    <div className="col-md-6">
      <FormGroup>
        <ControlLabel>Attachments</ControlLabel>
        <FormControl
          type="file"
          multiple
          onChange={handleFileChange}
        />
        <div style={{ marginTop: 10 }}>
          {formData.attachments.map((file, index) => (
            <div key={index}>
              {file.name}
              <Button bsStyle="link" onClick={() => removeAttachment(index)}>Remove</Button>
            </div>
          ))}
        </div>
      </FormGroup>
    </div>
  </div>

  {/* Submit & Reset Buttons */}
  <Button
    type="submit"
    disabled={!isIssueTypeSelected || loadingSubmit}
    bsStyle="primary"
    style={{ marginLeft: '15px' }}
  >
    {loadingSubmit ? 'Submitting...' : 'Submit'}
  </Button>{' '}
  <Button
    type="button"
    onClick={() => {
      setFormData({
        issueType: '',
        userEmail: '',
        userRole: '',
        browserName: '',
        comments: '',
        attachments: []
      });
      setIsIssueTypeSelected(false);
    }}
  >
    Reset
  </Button>
</form>
</div>


      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Export CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup>
            <ControlLabel>File Name</ControlLabel>
            <FormControl
              type="text"
              value={exportFileName}
              onChange={(e) => setExportFileName(e.target.value)}
              placeholder="e.g., FeedbackExport"
            />
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowExportModal(false)}>Cancel</Button>
          <Button onClick={handleExport} bsStyle="primary">
            {loadingExport ? 'Exporting...' : 'Export'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IssueReportForm;
